import { memo, useEffect, useRef } from "react";
import type { ECharts, EChartsOption } from "echarts";
import * as echarts from "echarts";
import { registerWprTheme } from "./wprTheme";

registerWprTheme();

type EChartEventHandler = (params: unknown) => void;

interface EChartProps {
  option: EChartsOption;
  height?: number;
  className?: string;
  onEvents?: Record<string, EChartEventHandler>;
}

function EChart({ option, height = 400, className, onEvents }: EChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ECharts | null>(null);
  const eventsRef = useRef<Record<string, EChartEventHandler>>({});

  useEffect(() => {
    if (!containerRef.current) return;
    chartRef.current = echarts.init(containerRef.current, "wpr", { renderer: "svg" });

    // zrender doesn't set viewBox on client-side SVGs.  Adding one lets
    // the print-CSS `max-width: 100%` rule scale the chart down to fit
    // the narrower A4 page without any JavaScript timing dependency.
    const syncViewBox = () => {
      const svg = containerRef.current?.querySelector("svg");
      if (svg) {
        const w = svg.getAttribute("width");
        const h = svg.getAttribute("height");
        if (w && h) svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
      }
    };

    const handleResize = () => {
      chartRef.current?.resize();
      syncViewBox();
    };

    syncViewBox(); // initial

    window.addEventListener("resize", handleResize);
    window.addEventListener("beforeprint", handleResize);
    window.addEventListener("afterprint", handleResize);

    // Also observe container size changes (e.g., when side nav opens/closes)
    // Debounce resize to wait until CSS transition completes (250ms + buffer)
    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 280);
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("beforeprint", handleResize);
      window.removeEventListener("afterprint", handleResize);
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    chartRef.current?.setOption(option, true);
  }, [option]);

  // Handle event registration
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    // Remove old event handlers
    for (const eventName of Object.keys(eventsRef.current)) {
      chart.off(eventName);
    }

    // Add new event handlers
    if (onEvents) {
      for (const [eventName, handler] of Object.entries(onEvents)) {
        chart.on(eventName, handler);
      }
      eventsRef.current = onEvents;
    } else {
      eventsRef.current = {};
    }
  }, [onEvents]);

  return <div ref={containerRef} className={className} style={{ width: "100%", height }} />;
}

export default memo(EChart);
